import styled from "@emotion/styled";
import { FormInstance, Form, Input, DatePicker, Switch, Select, Button, Row, Col, Typography, Card, Avatar, Image } from "antd";
import { FC, useState, useEffect, useMemo } from "react";
import { useGetProductList } from "hooks/useProduct";
import { useGetCampaignRewardTypes, useGetCampaignConditionTypes } from "hooks/useCampaign";
import { GetProductListData } from "services/interface";
import { PlusOutlined, MinusCircleOutlined, DownOutlined, CalendarOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { getProductById } from "services/productApi";
import { useAppSelector } from "store";

const { TextArea } = Input;
const { Option } = Select;

interface MainCreateCampaignProps {
  form: FormInstance;
  onFinish: (values: any) => void;
}

const MainCreateCampaign: FC<MainCreateCampaignProps> = ({
  form,
  onFinish,
}) => {
  const [resultType, setResultType] = useState<"discount" | "free_product" | undefined>("discount");
  const [discountUnit, setDiscountUnit] = useState<"amount" | "percentage">("amount");

  const contact = useAppSelector((state) => state.auth.contact);

  const conditions = Form.useWatch("conditions", form);
  const conditionsLength = Array.isArray(conditions) ? conditions.length : 0;

  const freeGifts = Form.useWatch("free_gifts", form);
  const freeGiftsLength = Array.isArray(freeGifts) ? freeGifts.length : 0;

  const { data: productData, isLoading: isLoadingProducts } = useGetProductList({
    is_active: true,
  });

  const { data: rewardTypesData, isLoading: isLoadingRewardTypes } = useGetCampaignRewardTypes();
  const { data: conditionTypesData, isLoading: isLoadingConditionTypes } = useGetCampaignConditionTypes();

  const products: GetProductListData[] = productData?.products || [];
  const rewardTypes = rewardTypesData?.data || [];
  const conditionTypes = conditionTypesData?.data || [];

  // กรองเฉพาะ discount types และ free_product
  const discountTypes = rewardTypes.filter(type =>
    type.value === "discount_amount" || type.value === "discount_percentage"
  );
  const hasDiscount = discountTypes.length > 0;
  const freeProductType = rewardTypes.find(type => type.value === "free_product");

  useEffect(() => {
    if (rewardTypes.length > 0 && !resultType) {
      const defaultType = hasDiscount ? "discount" : (freeProductType?.value ? "free_product" : undefined);
      if (defaultType) {
        setResultType(defaultType);
        form.setFieldsValue({ result_type: defaultType });
      }
    }
  }, [rewardTypes, form, freeProductType, hasDiscount]);

  useEffect(() => {
    // ตั้งค่าเริ่มต้นที่ backend ต้องการ
    form.setFieldsValue({
      campaign_type: form.getFieldValue("campaign_type") ?? "buy_x_get_y",
      status: "active", // เป็น active เสมอ
      max_usage: form.getFieldValue("max_usage") ?? 0,
      max_usage_per_user: form.getFieldValue("max_usage_per_user") ?? 1,
      factory_id: contact?.id || null, // เป็น factory ของฉันเสมอ
    });
  }, [form, contact?.id]);

  useEffect(() => {
    // ถ้ามีของแถมแค่ 1 แถว ให้ default decision เป็น END (แต่ไม่ทับถ้าผู้ใช้เคยเลือกเอง)
    if (resultType !== "free_product") return;
    if (freeGiftsLength !== 1) return;

    const decisionPath: any = ["free_gifts", 0, "decision"];
    const currentDecision = form.getFieldValue(decisionPath);
    const isTouched =
      form.isFieldsTouched?.([decisionPath], true) ??
      form.isFieldTouched?.(decisionPath) ??
      false;

    // - ถ้าค่าว่าง ให้ตั้ง END เสมอ (กันเคสถูก setFieldsValue บางจุดแล้วค่า decision หาย)
    // - ถ้าค่าเป็น AND ให้ตั้ง END เฉพาะตอนผู้ใช้ยังไม่เคยเลือกเอง
    const shouldSetEnd =
      currentDecision == null || (!isTouched && currentDecision === "AND");

    if (shouldSetEnd && currentDecision !== "END") {
      const currentList = form.getFieldValue("free_gifts") || [];
      if (Array.isArray(currentList) && currentList.length === 1) {
        form.setFieldsValue({
          free_gifts: [{ ...currentList[0], decision: "END" }],
        });
      }
    }
  }, [form, freeGiftsLength, resultType]);

  useEffect(() => {
    // ถ้ามีเงื่อนไขแค่ 1 แถว ให้แสดง/บังคับ decision เป็น "=" (แต่ไม่ทับถ้าผู้ใช้เคยเลือกเอง)
    if (conditionsLength !== 1) return;

    const decisionPath: any = ["conditions", 0, "decision"];
    const isTouched = form.isFieldTouched?.(decisionPath);
    const currentDecision = form.getFieldValue(decisionPath);

    if (!isTouched && currentDecision !== "=") {
      const currentList = form.getFieldValue("conditions") || [];
      if (Array.isArray(currentList) && currentList.length === 1) {
        form.setFieldsValue({
          conditions: [{ ...currentList[0], decision: "=" }],
        });
      }
    }
  }, [form, conditionsLength]);

  // Sync resultType state with campaign_type
  useEffect(() => {
    const campaignType = form.getFieldValue("campaign_type") || "buy_x_get_y";
    const newResultType = campaignType === "buy_x_get_y" ? "free_product" : "discount";
    setResultType(newResultType);

    // Set initial result_type in form if not set
    if (!form.getFieldValue("result_type")) {
      form.setFieldsValue({ result_type: newResultType });
    }
  }, [form]);

  const SkuVariantSelect: FC<{
    productId?: string;
    restField: any;
    name: number;
    listName: "conditions" | "free_gifts";
  }> = ({ productId, restField, name, listName }) => {
    const { data, isFetching } = useQuery({
      queryKey: ["product_by_id_for_campaign", productId],
      enabled: Boolean(productId),
      queryFn: async () => {
        const res: any = await getProductById(String(productId));
        return res;
      },
    });

    const options = useMemo(() => {
      return data?.product?.variants?.flatMap((variant: any) =>
        (variant?.items ?? []).map((item: any) => ({
          // Normalize to string to match AntD Select comparisons & form values
          value: String(item.id),
          label: `${variant.name} - ${item.name}`,
        }))
      ) ?? [];
    }, [data?.product?.variants]);

    const optionValues = useMemo(() => {
      return new Set(options.map((opt: any) => String(opt.value)));
    }, [options]);

    // Clear sku_variant_id when productId changes
    useEffect(() => {
      if (!productId) {
        // Clear when productId is cleared
        const currentList = form.getFieldValue(listName) || [];
        form.setFieldsValue({
          [listName]: currentList.map((item: any, idx: number) =>
            idx === name ? { ...item, sku_variant_id: undefined } : item
          ),
        });
        return;
      }

      if (data?.product && optionValues.size > 0) {
        const currentValue = form.getFieldValue([listName, name, "sku_variant_id"]);
        const normalizedCurrentValue = currentValue == null ? undefined : String(currentValue);

        // Normalize stored value to string so Select can display it reliably
        if (normalizedCurrentValue && currentValue !== normalizedCurrentValue) {
          const currentList = form.getFieldValue(listName) || [];
          form.setFieldsValue({
            [listName]: currentList.map((item: any, idx: number) =>
              idx === name ? { ...item, sku_variant_id: normalizedCurrentValue } : item
            ),
          });
        }

        if (normalizedCurrentValue && !optionValues.has(normalizedCurrentValue)) {
          // Current value is not valid in new options, clear it
          const currentList = form.getFieldValue(listName) || [];
          form.setFieldsValue({
            [listName]: currentList.map((item: any, idx: number) =>
              idx === name ? { ...item, sku_variant_id: undefined } : item
            ),
          });
        }
      }
    }, [productId, data?.product, optionValues, form, listName, name]);

    return (
      <Form.Item
        {...restField}
        name={[name, "sku_variant_id"]}
        label="SKU/Variant"
        rules={[{ required: true, message: "กรุณาเลือก SKU/Variant" }]}
        style={{ marginTop: 8, marginBottom: 0 }}
      >
        <Select
          placeholder={productId ? "เลือก SKU/Variant" : "กรุณาเลือกสินค้าก่อน"}
          disabled={!productId}
          loading={isFetching}
          suffixIcon={<DownOutlined />}
          options={options}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
    );
  };

  return (
    <FormWrapper>
      {/* Campaign Details Section */}
      <FormItemWrapper>
        <CardWrapper>
          <Form.Item
            label={<span>Campaign Name <span style={{ color: "red" }}>*</span></span>}
            name="campaign_name"
            rules={[{ required: true, message: "กรุณากรอกชื่อแคมเปญ" }]}
          >
            <Input placeholder="Campaign Name" size="small" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={3} placeholder="Description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={10}>
              <Form.Item
                label={<span>Start Date <span style={{ color: "red" }}>*</span></span>}
                name="start_date"
                rules={[{ required: true, message: "กรุณาเลือกวันที่เริ่มต้น" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  suffixIcon={<CalendarOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                label="Expire Date"
                name="expire_date"
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  suffixIcon={<CalendarOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Disable Campaign"
                name="is_disabled"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={<span>Campaign Type <span style={{ color: "red" }}>*</span></span>}
                name="campaign_type"
                rules={[{ required: true, message: "กรุณาเลือกประเภทแคมเปญ" }]}
                initialValue="buy_x_get_y"
              >
                <Select suffixIcon={<DownOutlined />}>
                  <Option value="buy_x_get_y">Buy X Get Y</Option>
                  <Option value="discount">Discount</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Max Usage"
                name="max_usage"
                initialValue={0}
                rules={[{ required: true, message: "กรุณากรอก max usage" }]}
              >
                <Input type="number" min={0} size="small" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Max Usage / User"
                name="max_usage_per_user"
                initialValue={1}
                rules={[{ required: true, message: "กรุณากรอก max usage ต่อผู้ใช้" }]}
              >
                <Input type="number" min={0} size="small" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>

            <Col span={24}>
              <Form.Item label="Remark" name="remark">
                <TextArea placeholder="หมายเหตุ" rows={4} />
              </Form.Item>
            </Col>
          </Row>

        </CardWrapper>
      </FormItemWrapper>

      {/* Conditions Section */}
      <FormItemWrapper>
        <CardWrapper>
          <Typography.Title level={5} style={{ marginBottom: 20 }}>เงื่อนไข</Typography.Title>

          <Form.Item
            label="ประเภทเงื่อนไข"
            name="condition_type"
            rules={[{ required: true, message: "กรุณาเลือกประเภทเงื่อนไข" }]}
            style={{ marginBottom: 20 }}
          >
            <Select
              placeholder="เลือกประเภทเงื่อนไข"
              loading={isLoadingConditionTypes}
              suffixIcon={<DownOutlined />}
            >
              {conditionTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.List name="conditions" initialValue={[{ quantity: 1, decision: "+" }]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <ConditionItem key={key}>
                    <Row gutter={16} align="bottom">
                      <Col flex="auto">
                        <Form.Item
                          {...restField}
                          name={[name, "product_id"]}
                          rules={[{ required: true, message: "กรุณาเลือกสินค้า" }]}
                          label="Product"
                          style={{ marginBottom: 0 }}
                        >
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, curValues) => {
                              const prevProductId = prevValues?.conditions?.[name]?.product_id;
                              const curProductId = curValues?.conditions?.[name]?.product_id;
                              return prevProductId !== curProductId;
                            }}
                          >
                            {() => {
                              const productId = form.getFieldValue(["conditions", name, "product_id"]);
                              const selectedProduct = products.find(p => p.id === productId);

                              return (
                                <div>
                                  <Select
                                    showSearch
                                    placeholder="เลือกสินค้า"
                                    optionFilterProp="children"
                                    loading={isLoadingProducts}
                                    filterOption={(input, option) =>
                                      String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    suffixIcon={<DownOutlined />}
                                    value={productId}
                                    onChange={(value) => {
                                      form.setFieldsValue({
                                        conditions: form.getFieldValue("conditions")?.map((item: any, idx: number) =>
                                          idx === name ? { ...item, product_id: value, sku_variant_id: undefined } : item
                                        ) || [{ product_id: value, sku_variant_id: undefined }],
                                      });
                                    }}
                                    dropdownRender={(menu) => (
                                      <div>
                                        {menu}
                                      </div>
                                    )}
                                  >
                                    {products.map((product) => (
                                      <Option key={product.id} value={product.id} label={product.name}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                          <Avatar
                                            src={product.image_url}
                                            size={40}
                                            shape="square"
                                            style={{ flexShrink: 0 }}
                                          />
                                          <span style={{ flex: 1 }}>{product.name}</span>
                                        </div>
                                      </Option>
                                    ))}
                                  </Select>
                         
                                  <SkuVariantSelect productId={productId} restField={restField} name={name} listName="conditions" />
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "quantity"]}
                          label="Quantity"
                          rules={[{ required: true, message: "กรุณากรอกจำนวน" }]}
                          initialValue={1}
                          style={{ marginBottom: 0 }}
                        >
                          <Input type="number" min={1} size="small" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          name={[name, "decision"]}
                          label="Decision"
                          initialValue="="
                          style={{ marginBottom: 0 }}
                        >
                          <Select suffixIcon={<DownOutlined />}>
                            <Option value="+">+</Option>
                            <Option value="=">=</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    {fields.length > 1 && (
                      <Button
                        type="link"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginTop: 8, padding: 0 }}
                      >
                        ลบเงื่อนไข
                      </Button>
                    )}
                  </ConditionItem>
                ))}
                <Row gutter={16} justify="center">
                  <Col span={6} >
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusOutlined />}
                      style={{ width: "100%", marginTop: 16 }}
                    >
                      เพิ่มเงื่อนไข
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Form.List>
        </CardWrapper>
      </FormItemWrapper>

      {/* Results Section */}
      <FormItemWrapper>
        <CardWrapper>
          <Typography.Title level={5} style={{ marginBottom: 20 }}>ผลลัพธ์</Typography.Title>

          {/* Hidden field เพื่อเก็บค่า result_type */}
          <Form.Item name="result_type" hidden>
            <Input />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.campaign_type !== cur.campaign_type || prev.result_type !== cur.result_type}>
            {() => {
              const campaignType = form.getFieldValue("campaign_type");
              const currentResultType = form.getFieldValue("result_type");

              // Update state if needed
              if (currentResultType && currentResultType !== resultType) {
                setResultType(currentResultType);
              }

              return (
                <>
                  <Typography.Text strong style={{ marginBottom: 16, display: "block", fontSize: 16 }}>
                    ประเภทผลลัพธ์: {campaignType === "buy_x_get_y" ? "ซื้อแล้วได้ของแถม" : "ซื้อแล้วได้ส่วนลด"}
                  </Typography.Text>

                  {currentResultType === "discount" && (
                    <div style={{ marginBottom: 16 }}>
                      <Form.Item
                        name="discount_value"
                        label="จำนวนส่วนลด"
                        style={{ display: "inline-block", margin: 0, marginRight: 8 }}
                        rules={currentResultType === "discount" ? [{ required: true, message: "กรุณากรอกจำนวน" }] : []}
                      >
                        <Input
                          type="number"
                          style={{ width: 150 }}
                          placeholder="จำนวน"
                          max={discountUnit === "percentage" ? 100 : undefined}
                          size="small"
                        />
                      </Form.Item>
                      <Form.Item
                        name="discount_unit"
                        style={{ display: "inline-block", margin: 0 }}
                        initialValue="amount"
                      >
                        <Select
                          value={discountUnit}
                          onChange={(value) => {
                            setDiscountUnit(value);
                            form.setFieldsValue({ discount_unit: value });
                          }}
                          style={{ width: 100 }}
                          suffixIcon={<DownOutlined />}
                        >
                          <Option value="amount">บาท</Option>
                          <Option value="percentage">%</Option>
                        </Select>
                      </Form.Item>
                    </div>
                  )}
                </>
              );
            }}
          </Form.Item>

          {resultType === "free_product" && (
            <Form.List name="free_gifts" initialValue={[{ quantity: 1, decision: "END" }]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <ConditionItem key={key}>
                      <Row gutter={16} align="bottom">
                        <Col flex="auto">
                          <Form.Item
                            {...restField}
                            name={[name, "product_id"]}
                            rules={[{ required: true, message: "กรุณาเลือกสินค้า" }]}
                            label="Product"
                            style={{ marginBottom: 0 }}
                          >
                            <Form.Item
                              noStyle
                              shouldUpdate={(prevValues, curValues) => {
                                const prevProductId = prevValues?.free_gifts?.[name]?.product_id;
                                const curProductId = curValues?.free_gifts?.[name]?.product_id;
                                return prevProductId !== curProductId;
                              }}
                            >
                              {() => {
                                const productId = form.getFieldValue(["free_gifts", name, "product_id"]);
                                const selectedProduct = products.find(p => p.id === productId);

                                return (
                                  <div>
                                    <Select
                                      showSearch
                                      placeholder="เลือกสินค้า"
                                      optionFilterProp="children"
                                      loading={isLoadingProducts}
                                      filterOption={(input, option) =>
                                        String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                      }
                                      suffixIcon={<DownOutlined />}
                                      value={productId}
                                      onChange={(value) => {
                                        form.setFieldsValue({
                                          free_gifts: form.getFieldValue("free_gifts")?.map((item: any, idx: number) =>
                                            idx === name ? { ...item, product_id: value, sku_variant_id: undefined } : item
                                          ) || [{ product_id: value, sku_variant_id: undefined }],
                                        });
                                      }}
                                      dropdownRender={(menu) => (
                                        <div>
                                          {menu}
                                        </div>
                                      )}
                                    >
                                      {products.map((product) => (
                                        <Option key={product.id} value={product.id} label={product.name}>
                                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <Avatar
                                              src={product.image_url}
                                              size={40}
                                              shape="square"
                                              style={{ flexShrink: 0 }}
                                            />
                                            <span style={{ flex: 1 }}>{product.name}</span>
                                          </div>
                                        </Option>
                                      ))}
                                    </Select>
                                
                                    <SkuVariantSelect productId={productId} restField={restField} name={name} listName="free_gifts" />
                                  </div>
                                );
                              }}
                            </Form.Item>
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "quantity"]}
                            label="Quantity"
                            rules={[{ required: true, message: "กรุณากรอกจำนวน" }]}
                            initialValue={1}
                            style={{ marginBottom: 0 }}
                          >
                            <Input type="number" min={1} size="small" />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, "decision"]}
                            label="Decision"
                            initialValue={"END"}
                            style={{ marginBottom: 0 }}
                          >
                            <Select suffixIcon={<DownOutlined />} >
                              <Option value="END">END</Option>
                              <Option value="AND">AND</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      {fields.length > 1 && (
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          style={{ marginTop: 8, padding: 0 }}
                        >
                          ลบของแถม
                        </Button>
                      )}
                    </ConditionItem>
                  ))}
                  <Row gutter={16} justify="center">
                    <Col span={6} >
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        style={{ width: "100%", marginTop: 16 }}
                      >
                        เพิ่มของแถม
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
            </Form.List>
          )}
        </CardWrapper>
      </FormItemWrapper>
    </FormWrapper>
  );
};

export default MainCreateCampaign;

const FormWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #efefefef;
`;

const FormItemWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px 0px 10px 0px;
  border-radius: 10px;
`;

const CardWrapper = styled.div`
  width: 100%;
  background-color: #ffffff;
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const ConditionItem = styled.div`
  width: 100%;
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
`;

const ProductSelectDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  min-height: 40px;
  position: relative;
  z-index: 0;
`;

const ProductThumbnail = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: #f5f5f5;
  border: 1px solid #e8e8e8;
`;

const ProductNameText = styled.span`
  flex: 1;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

